import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import type {
  AutoFillText,
  ParseResumeErrorCode,
  ParseResumeResponseBody,
  ParseResumeResponseData
} from '../types';

const PARSE_PROGRESS_START = 20;
const PARSE_PROGRESS_END = 80;
const PARSE_PROGRESS_DURATION_MS = 3000;
const PARSE_PROGRESS_INTERVAL_MS = 100;
const PARSE_PROGRESS_HIDE_DELAY_MS = 300;

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

interface UseResumeParserOptions {
  parseEndpoint?: string;
  text?: AutoFillText;
  onParsed: (data: ParseResumeResponseData) => void;
  onParsingProgressChange?: (progress: number, visible: boolean) => void;
  onParsingStateChange?: (parsing: boolean, error?: string) => void;
}

interface UseResumeParserResult {
  cancelActiveParse: () => void;
  clearParseFeedback: () => void;
  resolveErrorMessage: (errorCode?: ParseResumeErrorCode) => string;
  triggerParse: (file: File) => Promise<void>;
}

export const useResumeParser = ({
  parseEndpoint,
  text,
  onParsed,
  onParsingProgressChange,
  onParsingStateChange
}: UseResumeParserOptions): UseResumeParserResult => {
  const parseControllerRef = useRef<AbortController | null>(null);
  const parseSequenceRef = useRef(0);
  const progressIntervalRef = useRef<number | null>(null);
  const progressHideTimeoutRef = useRef<number | null>(null);

  // Keep latest callbacks in refs so the async triggerParse closure can use
  // current handlers without depending on callbacks that change during parsing.
  const onParsedRef = useRef(onParsed);
  const onParsingStateChangeRef = useRef(onParsingStateChange);

  useLayoutEffect(() => {
    onParsedRef.current = onParsed;
  }, [onParsed]);

  useLayoutEffect(() => {
    onParsingStateChangeRef.current = onParsingStateChange;
  }, [onParsingStateChange]);

  const resolveErrorMessage = useCallback(
    (errorCode?: ParseResumeErrorCode): string => {
      if (!text) {
        return '';
      }

      switch (errorCode) {
        case 'job_not_found':
          return text.errors.job_not_found;
        case 'file_required':
          return text.errors.file_required;
        case 'unsupported_file_format':
          return text.errors.unsupported_file_format;
        case 'file_too_large':
          return text.errors.file_too_large;
        case 'rate_limited':
          return text.errors.rate_limited;
        case 'text_extraction_failed':
          return text.errors.text_extraction_failed;
        case 'parse_failed':
        default:
          return text.errors.parse_failed;
      }
    },
    [text]
  );

  const stopProgressTimers = useCallback(() => {
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (progressHideTimeoutRef.current) {
      window.clearTimeout(progressHideTimeoutRef.current);
      progressHideTimeoutRef.current = null;
    }
  }, []);

  const hideParseProgress = useCallback(() => {
    stopProgressTimers();
    onParsingProgressChange?.(0, false);
  }, [onParsingProgressChange, stopProgressTimers]);

  const startParseProgress = useCallback(() => {
    stopProgressTimers();
    onParsingProgressChange?.(PARSE_PROGRESS_START, true);

    const startedAt = Date.now();
    progressIntervalRef.current = window.setInterval(() => {
      const elapsed = Math.min(Date.now() - startedAt, PARSE_PROGRESS_DURATION_MS);
      const progress =
        PARSE_PROGRESS_START +
        Math.round(
          (elapsed / PARSE_PROGRESS_DURATION_MS) * (PARSE_PROGRESS_END - PARSE_PROGRESS_START)
        );

      onParsingProgressChange?.(Math.min(progress, PARSE_PROGRESS_END), true);

      if (elapsed >= PARSE_PROGRESS_DURATION_MS && progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }, PARSE_PROGRESS_INTERVAL_MS);
  }, [onParsingProgressChange, stopProgressTimers]);

  const completeParseProgress = useCallback(() => {
    stopProgressTimers();
    onParsingProgressChange?.(100, true);
    progressHideTimeoutRef.current = window.setTimeout(() => {
      onParsingProgressChange?.(0, false);
      progressHideTimeoutRef.current = null;
    }, PARSE_PROGRESS_HIDE_DELAY_MS);
  }, [onParsingProgressChange, stopProgressTimers]);

  const clearParseFeedback = useCallback(() => {
    hideParseProgress();
    onParsingStateChange?.(false, '');
  }, [hideParseProgress, onParsingStateChange]);

  const cancelActiveParse = useCallback(() => {
    parseSequenceRef.current += 1;
    parseControllerRef.current?.abort();
    parseControllerRef.current = null;
    stopProgressTimers();
  }, [stopProgressTimers]);

  const triggerParse = useCallback(
    async (file: File) => {
      if (!parseEndpoint) {
        return;
      }

      cancelActiveParse();

      const controller = new AbortController();
      const sequence = parseSequenceRef.current + 1;
      parseSequenceRef.current = sequence;
      parseControllerRef.current = controller;

      onParsingStateChangeRef.current?.(true, '');
      startParseProgress();

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(parseEndpoint, {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });
        const body = (await response.json().catch(() => ({}))) as ParseResumeResponseBody;

        if (sequence !== parseSequenceRef.current) {
          return;
        }

        if (!response.ok || !body.success || !body.data) {
          throw new Error(resolveErrorMessage(body.error_code));
        }

        onParsedRef.current(body.data);
        parseControllerRef.current = null;
        completeParseProgress();
        onParsingStateChangeRef.current?.(false, '');
      } catch (error) {
        if (sequence !== parseSequenceRef.current) {
          return;
        }

        if (isAbortError(error)) {
          return;
        }

        parseControllerRef.current = null;
        hideParseProgress();
        onParsingStateChangeRef.current?.(
          false,
          error instanceof Error && error.message
            ? error.message
            : resolveErrorMessage('parse_failed')
        );
      }
    },
    [
      cancelActiveParse,
      completeParseProgress,
      hideParseProgress,
      parseEndpoint,
      resolveErrorMessage,
      startParseProgress
    ]
  );

  useEffect(
    () => () => {
      cancelActiveParse();
      hideParseProgress();
    },
    [cancelActiveParse, hideParseProgress]
  );

  return {
    cancelActiveParse,
    clearParseFeedback,
    resolveErrorMessage,
    triggerParse
  };
};
